/**
 * Theme utilities for custom theme editor
 *
 * Supports two layers of customization:
 * 1. Brand palette (vizimix-100..900) — base color shades
 * 2. Semantic colors (background, primary, card, etc.) — ShadCN-style tokens
 */

// =============================================================================
// Default brand palette
// =============================================================================

export const DEFAULT_DARK_COLORS = {
  "vizimix-900": "#1e1b4b",
  "vizimix-800": "#312e81",
  "vizimix-700": "#3730a3",
  "vizimix-600": "#4f46e5",
  "vizimix-500": "#6366f1",
  "vizimix-400": "#818cf8",
  "vizimix-300": "#a5b4fc",
  "vizimix-200": "#c7d2fe",
  "vizimix-100": "#e0e7ff",
} as const;

export const DEFAULT_LIGHT_COLORS = {
  "vizimix-900": "#f8fafc",
  "vizimix-800": "#f1f5f9",
  "vizimix-700": "#e2e8f0",
  "vizimix-600": "#94a3b8",
  "vizimix-500": "#6366f1",
  "vizimix-400": "#4f46e5",
  "vizimix-300": "#334155",
  "vizimix-200": "#1e293b",
  "vizimix-100": "#0f172a",
} as const;

// =============================================================================
// Default semantic colors (ShadCN tokens)
// =============================================================================

export const DEFAULT_SEMANTIC_COLORS = {
  // Page background & text
  background: "#1e1b4b",
  foreground: "#e0e7ff",
  // Card / panel surfaces
  card: "#312e81",
  "card-foreground": "#e0e7ff",
  // Popover / dropdown surfaces
  popover: "#312e81",
  "popover-foreground": "#e0e7ff",
  // Primary action (buttons, links)
  primary: "#6366f1",
  "primary-foreground": "#ffffff",
  // Secondary action
  secondary: "#3730a3",
  "secondary-foreground": "#e0e7ff",
  // Muted / subtle elements
  muted: "#312e81",
  "muted-foreground": "#a5b4fc",
  // Accent / highlight
  accent: "#4f46e5",
  "accent-foreground": "#e0e7ff",
  // Destructive / danger
  destructive: "#dc2626",
  "destructive-foreground": "#ffffff",
  // Borders, inputs, focus ring
  border: "#3730a3",
  input: "#3730a3",
  ring: "#6366f1",
  // Sidebar
  "sidebar-background": "#1e1b4b",
  "sidebar-foreground": "#c7d2fe",
  "sidebar-primary": "#6366f1",
  "sidebar-primary-foreground": "#ffffff",
  "sidebar-accent": "#312e81",
  "sidebar-accent-foreground": "#e0e7ff",
  "sidebar-border": "#3730a3",
  // Chart colors
  "chart-1": "#6366f1",
  "chart-2": "#818cf8",
  "chart-3": "#a5b4fc",
  "chart-4": "#4f46e5",
  "chart-5": "#c7d2fe",
} as const;

export const DEFAULT_LIGHT_SEMANTIC_COLORS: Record<string, string> = {
  background: "#ffffff",
  foreground: "#0f172a",
  card: "#f8fafc",
  "card-foreground": "#0f172a",
  popover: "#ffffff",
  "popover-foreground": "#0f172a",
  primary: "#6366f1",
  "primary-foreground": "#ffffff",
  secondary: "#f1f5f9",
  "secondary-foreground": "#0f172a",
  muted: "#f1f5f9",
  "muted-foreground": "#64748b",
  accent: "#f1f5f9",
  "accent-foreground": "#0f172a",
  destructive: "#dc2626",
  "destructive-foreground": "#ffffff",
  border: "#e2e8f0",
  input: "#e2e8f0",
  ring: "#6366f1",
  "sidebar-background": "#f8fafc",
  "sidebar-foreground": "#334155",
  "sidebar-primary": "#6366f1",
  "sidebar-primary-foreground": "#ffffff",
  "sidebar-accent": "#f1f5f9",
  "sidebar-accent-foreground": "#0f172a",
  "sidebar-border": "#e2e8f0",
  "chart-1": "#6366f1",
  "chart-2": "#818cf8",
  "chart-3": "#a5b4fc",
  "chart-4": "#4f46e5",
  "chart-5": "#c7d2fe",
};

export type SemanticColorKey = keyof typeof DEFAULT_SEMANTIC_COLORS;
export type BrandColorKey = keyof typeof DEFAULT_DARK_COLORS;

// =============================================================================
// Semantic color metadata for the editor UI
// =============================================================================

export interface ColorGroup {
  label: string;
  description: string;
  keys: SemanticColorKey[];
}

export const SEMANTIC_COLOR_GROUPS: ColorGroup[] = [
  {
    label: "Background & Surface",
    description: "Page background, text color, card panels, and popovers",
    keys: ["background", "foreground", "card", "card-foreground", "popover", "popover-foreground"],
  },
  {
    label: "Primary & Secondary",
    description: "Primary action buttons, links, and secondary elements",
    keys: ["primary", "primary-foreground", "secondary", "secondary-foreground"],
  },
  {
    label: "Muted & Accent",
    description: "Subtle text, ghost/outline button hovers, highlighted items",
    keys: ["muted", "muted-foreground", "accent", "accent-foreground"],
  },
  {
    label: "Destructive",
    description: "Delete buttons, error states, and danger indicators",
    keys: ["destructive", "destructive-foreground"],
  },
  {
    label: "Borders & Input",
    description: "Card borders, dividers, input borders, and focus rings",
    keys: ["border", "input", "ring"],
  },
  {
    label: "Sidebar",
    description: "Sidebar navigation panel colors",
    keys: [
      "sidebar-background", "sidebar-foreground",
      "sidebar-primary", "sidebar-primary-foreground",
      "sidebar-accent", "sidebar-accent-foreground",
      "sidebar-border",
    ],
  },
  {
    label: "Chart Colors",
    description: "Data visualization and chart series colors",
    keys: ["chart-1", "chart-2", "chart-3", "chart-4", "chart-5"],
  },
];

// =============================================================================
// Token → component mapping (for editor legend)
// =============================================================================

export const TOKEN_COMPONENT_MAP: Record<string, string> = {
  background: "Page background, scrollbar track, modal backdrop",
  foreground: "Body text, headings, input text",
  card: "Cards, panels, sidebar items, dialog backgrounds",
  "card-foreground": "Card title and body text",
  popover: "Dropdown menus, tooltips, dialog surfaces",
  "popover-foreground": "Popover text",
  primary: "Default buttons, links, switch (checked), progress bars, focus rings",
  "primary-foreground": "Text on primary-colored backgrounds",
  secondary: "Secondary buttons, tags, inactive pills",
  "secondary-foreground": "Text on secondary backgrounds",
  muted: "Subtle backgrounds, skeleton loaders",
  "muted-foreground": "Help text, timestamps, placeholder text, icons",
  accent: "Ghost/outline button hover, highlighted items, download buttons",
  "accent-foreground": "Text on accent backgrounds",
  destructive: "Delete buttons, error states, danger indicators",
  "destructive-foreground": "Text on destructive backgrounds",
  border: "Card borders, dividers, section separators",
  input: "Input field borders, switch (unchecked) track",
  ring: "Focus ring color on inputs, buttons, and interactive elements",
};

// =============================================================================
// Theme interface
// =============================================================================

export interface RadiusConfig {
  button?: string;   // default "0.5rem" (8px)
  card?: string;     // default "1rem" (16px)
  panel?: string;    // default "0.75rem" (12px)
  input?: string;    // default "0.5rem" (8px)
  bubble?: string;   // default "1rem" (16px)
}

export const DEFAULT_RADIUS_CONFIG: RadiusConfig = {
  button: "0.5rem",
  card: "1rem",
  panel: "0.75rem",
  input: "0.5rem",
  bubble: "1rem",
};

export interface CustomTheme {
  id: string;
  name: string;
  mode: "light" | "dark";
  colors: Record<BrandColorKey, string>;
  /** ShadCN semantic color overrides (optional for backwards compat) */
  semanticColors?: Partial<Record<SemanticColorKey, string>>;
  /** Border radius override (e.g., "0.5rem") — legacy, prefer radiusConfig */
  radius?: string;
  /** Per-component border radius overrides */
  radiusConfig?: RadiusConfig;
  /** Whether this is a built-in preset theme (cannot be deleted) */
  isPreset?: boolean;
}

export function generateThemeId(): string {
  return crypto.randomUUID();
}

const CUSTOM_THEME_KEY = "vizimix_custom_theme";

// =============================================================================
// Apply / remove theme
// =============================================================================

/**
 * Apply a custom theme to the document by setting CSS variables
 */
export function applyCustomTheme(theme: CustomTheme | null): void {
  const root = document.documentElement;

  if (!theme) {
    // Remove all custom properties
    Object.keys(DEFAULT_DARK_COLORS).forEach((key) => {
      root.style.removeProperty(`--color-${key}`);
    });
    Object.keys(DEFAULT_SEMANTIC_COLORS).forEach((key) => {
      root.style.removeProperty(`--color-${key}`);
    });
    root.style.removeProperty("--radius");
    root.style.removeProperty("--radius-sm");
    root.style.removeProperty("--radius-lg");
    root.style.removeProperty("--radius-button");
    root.style.removeProperty("--radius-card");
    root.style.removeProperty("--radius-panel");
    root.style.removeProperty("--radius-input");
    root.style.removeProperty("--radius-bubble");
    return;
  }

  // Apply brand palette colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });

  // Apply semantic colors
  if (theme.semanticColors) {
    Object.entries(theme.semanticColors).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--color-${key}`, value);
      }
    });
  }

  // Apply per-component radius
  if (theme.radiusConfig) {
    const rc = theme.radiusConfig;
    if (rc.button) root.style.setProperty("--radius-button", rc.button);
    if (rc.card) root.style.setProperty("--radius-card", rc.card);
    if (rc.panel) root.style.setProperty("--radius-panel", rc.panel);
    if (rc.input) root.style.setProperty("--radius-input", rc.input);
    if (rc.bubble) root.style.setProperty("--radius-bubble", rc.bubble);
  } else if (theme.radius) {
    // Backwards compat: apply legacy single radius to all components
    root.style.setProperty("--radius-button", theme.radius);
    root.style.setProperty("--radius-card", theme.radius);
    root.style.setProperty("--radius-panel", theme.radius);
    root.style.setProperty("--radius-input", theme.radius);
    root.style.setProperty("--radius-bubble", theme.radius);
  }

  // Apply legacy radius vars (still used by some ShadCN defaults)
  if (theme.radius) {
    root.style.setProperty("--radius", theme.radius);
    const rem = parseFloat(theme.radius);
    root.style.setProperty("--radius-sm", `${Math.max(0, rem - 0.25)}rem`);
    root.style.setProperty("--radius-lg", `${rem + 0.25}rem`);
  } else {
    root.style.removeProperty("--radius");
    root.style.removeProperty("--radius-sm");
    root.style.removeProperty("--radius-lg");
  }
}

/**
 * Get the effective semantic color value (override or default)
 */
export function getSemanticColor(
  theme: CustomTheme,
  key: SemanticColorKey
): string {
  if (theme.semanticColors?.[key]) return theme.semanticColors[key];
  const defaults = theme.mode === "light" ? DEFAULT_LIGHT_SEMANTIC_COLORS : DEFAULT_SEMANTIC_COLORS;
  return (defaults as Record<string, string>)[key] || DEFAULT_SEMANTIC_COLORS[key];
}

// =============================================================================
// Persistence
// =============================================================================

export function saveCustomTheme(theme: CustomTheme | null): void {
  try {
    if (theme) {
      localStorage.setItem(CUSTOM_THEME_KEY, JSON.stringify(theme));
    } else {
      localStorage.removeItem(CUSTOM_THEME_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

export function loadCustomTheme(): CustomTheme | null {
  try {
    const saved = localStorage.getItem(CUSTOM_THEME_KEY);
    if (saved) {
      return JSON.parse(saved) as CustomTheme;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

// =============================================================================
// Color conversions
// =============================================================================

export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace(/^#/, "");

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// =============================================================================
// Theme generation
// =============================================================================

/**
 * Derive semantic token values from a brand palette.
 * Reusable mapping: brand shades → semantic colors.
 */
export function deriveSemanticFromBrand(
  colors: Record<string, string>
): Partial<Record<SemanticColorKey, string>> {
  return {
    background: colors["vizimix-900"],
    foreground: colors["vizimix-100"],
    card: colors["vizimix-800"],
    "card-foreground": colors["vizimix-100"],
    popover: colors["vizimix-800"],
    "popover-foreground": colors["vizimix-100"],
    primary: colors["vizimix-500"],
    "primary-foreground": "#ffffff",
    secondary: colors["vizimix-700"],
    "secondary-foreground": colors["vizimix-100"],
    muted: colors["vizimix-800"],
    "muted-foreground": colors["vizimix-300"],
    accent: colors["vizimix-600"],
    "accent-foreground": colors["vizimix-100"],
    destructive: "#dc2626",
    "destructive-foreground": "#ffffff",
    border: colors["vizimix-700"],
    input: colors["vizimix-700"],
    ring: colors["vizimix-500"],
    "sidebar-background": colors["vizimix-900"],
    "sidebar-foreground": colors["vizimix-200"],
    "sidebar-primary": colors["vizimix-500"],
    "sidebar-primary-foreground": "#ffffff",
    "sidebar-accent": colors["vizimix-800"],
    "sidebar-accent-foreground": colors["vizimix-100"],
    "sidebar-border": colors["vizimix-700"],
    "chart-1": colors["vizimix-500"],
    "chart-2": colors["vizimix-400"],
    "chart-3": colors["vizimix-300"],
    "chart-4": colors["vizimix-600"],
    "chart-5": colors["vizimix-200"],
  };
}

/**
 * Generate a random harmonious theme based on a base hue
 */
export function generateRandomTheme(baseHue?: number): CustomTheme {
  const hue = baseHue ?? Math.floor(Math.random() * 360);

  // Generate 9 brand shades from lightest (100) to darkest (900)
  const lightnesses = [95, 90, 82, 70, 60, 50, 40, 30, 15];
  const saturations = [30, 35, 45, 55, 65, 70, 70, 65, 50];

  const colors: Record<string, string> = {};

  lightnesses.forEach((l, i) => {
    const shade = (9 - i) * 100; // 900, 800, 700... 100
    const key = `vizimix-${shade}` as BrandColorKey;
    colors[key] = hslToHex(hue, saturations[i], l);
  });

  return {
    id: generateThemeId(),
    name: `Random ${Math.floor(hue)}`,
    mode: "dark" as const,
    colors: colors as CustomTheme["colors"],
    semanticColors: deriveSemanticFromBrand(colors),
  };
}

// =============================================================================
// Preset themes
// =============================================================================

/** Stable IDs for the default designer themes */
export const DEFAULT_LIGHT_THEME_ID = "0fca2a70-ac39-4cbf-9a5a-c13749df9bec";
export const DEFAULT_DARK_THEME_ID = "bed273ff-9dad-4ab7-ae81-e17410071611";

/**
 * Built-in preset themes available to all users.
 * These use stable IDs so they persist across sessions and can't be deleted.
 * The Vizi Light/Dark themes are auto-applied as defaults for Light/Dark mode.
 */
export const PRESET_THEMES: CustomTheme[] = [
  // Classic Dark — matches the original default dark CSS
  {
    id: "preset-classic-dark",
    name: "Classic Dark",
    mode: "dark",
    isPreset: true,
    colors: { ...DEFAULT_DARK_COLORS },
    semanticColors: { ...DEFAULT_SEMANTIC_COLORS },
  },
  // Classic Light — matches the original default light CSS
  {
    id: "preset-classic-light",
    name: "Classic Light",
    mode: "light",
    isPreset: true,
    colors: { ...DEFAULT_LIGHT_COLORS },
    semanticColors: { ...DEFAULT_LIGHT_SEMANTIC_COLORS } as Partial<Record<SemanticColorKey, string>>,
  },
  // Vizi Light — designer theme (default for Light mode)
  {
    id: DEFAULT_LIGHT_THEME_ID,
    name: "Vizi Light",
    mode: "light",
    isPreset: true,
    colors: {
      "vizimix-900": "#f8fafc",
      "vizimix-800": "#f1f5f9",
      "vizimix-700": "#e2e8f0",
      "vizimix-600": "#94a3b8",
      "vizimix-500": "#6366f1",
      "vizimix-400": "#4f46e5",
      "vizimix-300": "#334155",
      "vizimix-200": "#1e293b",
      "vizimix-100": "#0f172a",
    },
    semanticColors: {
      background: "#f7f7f7",
      foreground: "#393836",
      card: "#EBE9E3",
      "card-foreground": "#393836",
      popover: "#f7f7f7",
      "popover-foreground": "#393836",
      primary: "#538DBC",
      "primary-foreground": "#f7f7f7",
      secondary: "#FEA178",
      "secondary-foreground": "#393836",
      muted: "#E8E6E2",
      "muted-foreground": "#72716D",
      accent: "#D4E3EE",
      "accent-foreground": "#393836",
      destructive: "#EE3B23",
      "destructive-foreground": "#f7f7f7",
      border: "#929495",
      input: "#FFE0D2",
      ring: "#72716D",
      "sidebar-background": "#F1F3F3",
      "sidebar-foreground": "#393836",
      "sidebar-primary": "#538DBC",
      "sidebar-primary-foreground": "#f7f7f7",
      "sidebar-accent": "#A9C6DE",
      "sidebar-accent-foreground": "#393836",
      "sidebar-border": "#929495",
      "chart-1": "#FFC0A5",
      "chart-2": "#FEA178",
      "chart-3": "#FE814B",
      "chart-4": "#BF6138",
      "chart-5": "#7F4126",
    },
  },
  // Vizi Dark — designer theme (default for Dark mode)
  {
    id: DEFAULT_DARK_THEME_ID,
    name: "Vizi Dark",
    mode: "dark",
    isPreset: true,
    colors: {
      "vizimix-900": "#1e1b4b",
      "vizimix-800": "#312e81",
      "vizimix-700": "#3730a3",
      "vizimix-600": "#4f46e5",
      "vizimix-500": "#6366f1",
      "vizimix-400": "#818cf8",
      "vizimix-300": "#a5b4fc",
      "vizimix-200": "#c7d2fe",
      "vizimix-100": "#e0e7ff",
    },
    semanticColors: {
      background: "#131516",
      foreground: "#f2f0ec",
      card: "#15232F",
      "card-foreground": "#e6e7e7",
      popover: "#2a475e",
      "popover-foreground": "#e6e7e7",
      primary: "#538DBC",
      "primary-foreground": "#090a0b",
      secondary: "#ABA9A3",
      "secondary-foreground": "#090a0b",
      muted: "#292723",
      "muted-foreground": "#ABA9A3",
      accent: "#2A475E",
      "accent-foreground": "#e6e7e7",
      destructive: "#ee3b23",
      "destructive-foreground": "#090a0b",
      border: "#3E6A8D",
      input: "#E6E7E7",
      ring: "#FE814B",
      "sidebar-background": "#1c1f20",
      "sidebar-foreground": "#d1cec5",
      "sidebar-primary": "#538DBC",
      "sidebar-primary-foreground": "#090a0b",
      "sidebar-accent": "#2A475E",
      "sidebar-accent-foreground": "#090a0b",
      "sidebar-border": "#7a7568",
      "chart-1": "#FFC0A5",
      "chart-2": "#FEA178",
      "chart-3": "#FE814B",
      "chart-4": "#BF6138",
      "chart-5": "#7F4126",
    },
    radiusConfig: {
      button: "1.5rem",
      card: "0.6rem",
      panel: "0.6rem",
      input: "0.6rem",
      bubble: "1.5rem",
    },
  },
];

/** Check if a theme is a built-in preset (cannot be deleted) */
export function isPresetTheme(id: string): boolean {
  return PRESET_THEMES.some((t) => t.id === id);
}

/** Get the default designer theme for a given mode */
export function getDefaultThemeForMode(mode: "light" | "dark"): CustomTheme | undefined {
  const id = mode === "light" ? DEFAULT_LIGHT_THEME_ID : DEFAULT_DARK_THEME_ID;
  return PRESET_THEMES.find((t) => t.id === id);
}

// =============================================================================
// Import / export
// =============================================================================

export function exportTheme(theme: CustomTheme): string {
  return JSON.stringify(theme, null, 2);
}

export function importTheme(json: string): CustomTheme | null {
  try {
    const parsed = JSON.parse(json) as CustomTheme;

    if (!parsed.name || !parsed.colors) {
      throw new Error("Invalid theme structure");
    }

    // Validate brand color keys exist
    const requiredKeys = [
      "vizimix-100", "vizimix-200", "vizimix-300",
      "vizimix-400", "vizimix-500", "vizimix-600",
      "vizimix-700", "vizimix-800", "vizimix-900",
    ];

    for (const key of requiredKeys) {
      if (
        !(key in parsed.colors) ||
        typeof parsed.colors[key as keyof typeof parsed.colors] !== "string"
      ) {
        throw new Error(`Missing or invalid color: ${key}`);
      }
    }

    // semanticColors is optional — accept themes with or without them
    // Ensure id and mode exist (backwards compat with older exports)
    if (!parsed.id) parsed.id = generateThemeId();
    if (!parsed.mode) parsed.mode = "dark";
    return parsed;
  } catch {
    return null;
  }
}
